import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaCheckCircle, FaToolbox, FaShieldAlt, FaTruck } from 'react-icons/fa';
import { vehicleService } from '../services/vehicleService';
import { sampleTrailers } from '../data/sampleTrailers';

const Trailers = () => {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all'); // all | new | used
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Get available trailers (excludes sold)
        const { data, error: fetchError } = await vehicleService.getAvailableVehiclesByType(['trailer', 'trailers']);
        if (cancelled) return;
        
        if (fetchError) {
          console.error('Trailers page: Error loading:', fetchError);
          setError('temporarily_unavailable');
          setTrailers([]);
          return;
        }
        
        if (data && data.length > 0) {
          console.log('Trailers page: Found trailers in database:', data);
          setTrailers(data);
        } else {
          console.log('Trailers page: No trailers in database');
          setTrailers([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Trailers page: Exception:', err);
          setError('temporarily_unavailable');
          setTrailers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return trailers.filter(t => {
      const matchesCategory = selectedCategory === 'all' || 
        (selectedCategory === 'pre-owned' ? ((t?.condition || '').toLowerCase() === 'used' || (t?.condition || '').toLowerCase() === 'pre-owned') : 
        (t?.condition || '').toLowerCase() === selectedCategory);
      const query = search.trim().toLowerCase();
      const matchesSearch =
        query.length === 0 ||
        `${t?.year || ''} ${t?.make || ''} ${t?.model || ''}`.toLowerCase().includes(query) ||
        (t?.description || '').toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [trailers, selectedCategory, search]);

  const placeholders = [
    {
      title: 'Utility Trailer 7x16',
      description: 'Versatile tandem-axle utility trailer for work or weekend projects',
      image: '/assets/assets/images/business-shop-sign.webp',
      price: null,
      link: '/inventory',
    },
    {
      title: 'Car Hauler 20ft',
      description: 'Low-profile car hauler with ramps and stake pockets',
      image: '/assets/assets/images/business-shop-sign.webp',
      price: null,
      link: '/inventory',
    },
    {
      title: 'Equipment Trailer 14k',
      description: 'Heavy-duty equipment trailer with brakes and heavy ramps',
      image: '/assets/assets/images/business-shop-sign.webp',
      price: null,
      link: '/inventory',
    },
  ];

  const cards = filtered.length > 0 ? filtered : (loading ? [] : (error ? [] : placeholders));

  if (error === 'temporarily_unavailable') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 pt-24">
        <div className="container mx-auto px-4">
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
            <div className="text-6xl mb-6">🔧</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Trailers Temporarily Unavailable</h1>
            <p className="text-gray-600 mb-2">We're experiencing a brief technical issue with our inventory system.</p>
            <p className="text-gray-600 mb-8">Our trailers are still available — give us a call!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+18173822316"
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                📞 Call Us: (817) 382-2316
              </a>
              <button 
                onClick={() => window.location.reload()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Trailers for Sale in Paradise, TX | Utility, Enclosed, Dump | Paradise-Performance</title>
        <meta name="description" content="Shop utility trailers, enclosed trailers, dump trailers, and equipment trailers in Paradise, TX. Various sizes and capacities available. Financing options. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/trailers" />
        <meta property="og:title" content="Trailers for Sale - Paradise-Performance" />
        <meta property="og:description" content="Quality trailers for all your hauling needs. Multiple sizes available." />
        <meta property="og:url" content="https://paradise-performance.com/trailers" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gray-900 text-white py-16  pt-24 lg:pt-36 md:pt-16 sm:pt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Trailers
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Utility • Car Haulers • Equipment
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#inventory" className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded font-semibold transition-colors">
                Browse Trailers
              </a>
              <a href="tel:9406832335" className="px-6 py-3 border-2 border-white text-white rounded font-semibold hover:bg-white hover:text-gray-900 transition-colors">
                Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">Why Buy Trailers From Us?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaTruck className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Work-Ready Options</h3>
              <p className="text-sm text-gray-600">Utility, car hauler, equipment</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaToolbox className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Service & Upgrades</h3>
              <p className="text-sm text-gray-600">Ramps, brakes, lighting</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaShieldAlt className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Inspected & Ready</h3>
              <p className="text-sm text-gray-600">Quality checked inventory</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaCheckCircle className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Financing Available</h3>
              <p className="text-sm text-gray-600">Flexible options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section id="inventory" className="py-6 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-center sm:justify-start">
              {['all', 'new', 'pre-owned'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2 rounded font-medium transition-colors ${
                    selectedCategory === cat ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All Trailers' : cat === 'pre-owned' ? 'Pre-Owned' : 'New'}
                </button>
              ))}
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trailers..."
              className="w-full sm:w-80 px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 bg-white rounded-lg shadow-sm animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((t, idx) => {
                // Get the first available image from various possible sources
                let img = null;
                
                // Try images array first (most common for uploaded content)
                if (t?.images && Array.isArray(t.images) && t.images.length > 0) {
                  img = t.images[0];
                }
                // Try single image field
                else if (t?.image) {
                  img = t.image;
                }
                
                // Use fallback if no image found
                if (!img || img === '') {
                  img = '/assets/assets/images/business-shop-sign.webp';
                }
                
                const title = t?.id ? `${t?.year || ''} ${t?.make || ''} ${t?.model || 'Trailer'}`.trim() : t.title;
                const price = t?.price ? `$${Number(t.price).toLocaleString()}` : t.price === 0 ? '$0' : (t.price === null || t.price === undefined) ? (t.priceLabel || '') : `$${Number(t.price).toLocaleString()}`;
                const link = t?.id ? `/trailer/${t.id}` : t.link;
                const condition = (t?.condition || '').toLowerCase();
                return (
                  <div key={t?.id || idx} className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
                    <div className="relative">
                      <img src={img} alt={title} className="w-full h-56 object-cover" />
                      {condition && (
                        <span className={`absolute top-3 left-3 px-3 py-1 rounded text-white text-sm font-medium ${
                          condition === 'new' ? 'bg-green-600' : 'bg-blue-600'
                        }`}>
                          {condition.toUpperCase()}
                        </span>
                      )}
                      {price && (
                        <span className="absolute top-3 right-3 bg-white px-3 py-1 rounded font-bold text-lg">
                          {price}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{title}</h3>
                      
                      {/* Trailer Specifications */}
                      <div className="text-sm text-gray-600 mb-2 space-y-1">
                        {(t?.length || t?.width || t?.capacity) && (
                          <div className="font-medium">
                            {t.length && `${t.length}'`}
                            {t.width && ` x ${t.width}'`}
                            {t.capacity && ` • ${t.capacity} lbs capacity`}
                          </div>
                        )}
                        {/* Only show mileage for used trailers, not new ones */}
                        {t?.mileage !== undefined && t?.mileage !== null && t?.condition?.toLowerCase() !== 'new' && (
                          <div>Mileage: {t.mileage.toLocaleString()} mi</div>
                        )}
                        {t?.vin && (
                          <div className="text-xs font-mono text-gray-500">VIN: {t.vin}</div>
                        )}
                      </div>
                      
                      {t?.description && (
                        <p className="text-sm text-gray-600 mb-3">{t.description}</p>
                      )}
                      <Link to={link}>
                        <button className="w-full bg-gray-900 text-white py-2 px-4 rounded font-medium hover:bg-gray-800 transition-colors">
                          View Details
                        </button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need a Specific Trailer?</h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            Tell us what you need—size, capacity, features—and we’ll help you find it fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="px-6 py-3 bg-white text-primary-600 rounded font-semibold hover:bg-gray-100 transition-colors">
              Contact Us
            </a>
            <a href="/finance-application" className="px-6 py-3 border-2 border-white text-white rounded font-semibold hover:bg-white hover:text-primary-600 transition-colors">
              Financing Options
            </a>
          </div>
        </div>
      </section>
      {/* Policy Notice */}
      <div className="container mx-auto px-4 py-4 text-center">
        <p className="text-xs text-gray-500">
          All deposits are non-refundable. Please review our{' '}
          <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">
            Refund & Return Policy
          </Link>{' '}
          before making a purchase.
        </p>
      </div>
    </div>
  );
};

export default Trailers;
